<template>
    <v-layout column>
        <div v-if="ready">
            <div v-if="!hideNav" class="headline mb-3">
                District Information
            </div>

            <div class="d-flex">
                <fe-label class="label">District Name</fe-label>
                <v-text-field
                    v-model="info.name"
                    :rules="requiredRule"
                    validateOnBlur
                    flat
                    solo
                    dense
                    @change="update"
                />
            </div>

            <div class="d-flex mb-3">
                <fe-label class="label">District Logo</fe-label>
                <v-img
                    :src="$axios.defaults.baseURL + 'logo.php?' + random"
                    max-height="50"
                    max-width="50"
                    class="mr-4"
                />
                <fe-btn @click="$refs.logoInput.click()">
                    Upload Logo
                </fe-btn>
                <input
                    name="logoFile"
                    type="file"
                    ref="logoInput"
                    style="display: none"
                    @change="handleLogoUpload"
                />
                <fe-info-tip
                    tooltip="After uploading the file, the Logo Url path will change. You may have to clear the cached image by holding shift while refreshing the page to see any changes in forms and charts. Maximum file size is 1mb."
                />
            </div>

            <div style="font-size: 14px;"><b>Customize your District's Login Message</b></div>
            <fe-editor
                v-model="info.login_message"
                ref="editor"
                :toolbarExtras="['|', 'fontFamily', 'alignment', '|', 'redo', 'undo']"
                :showRemainingCount="false"
                :showErrorMsg="false"
                :characterLimit="500000"
                submittable
                @input="editorInput"
                @submit="update"
            />

            <v-row class="flex-grow-1 mt-3">
                <v-col cols="6">
                    <div v-if="manageDistrict" class="d-flex">
                        <fe-label class="label">Login ID</fe-label>
                        <v-text-field
                            v-model="info.login_id"
                            flat solo dense
                            @change="update"
                        />
                    </div>
                    <div class="d-flex">
                        <fe-label class="label">Street</fe-label>
                        <v-text-field v-model="info.street" flat solo dense @change="update"/>
                    </div>
                    <div class="d-flex">
                        <fe-label class="label">City</fe-label>
                        <v-text-field v-model="info.city" flat solo dense @change="update"/>
                    </div>
                    <div class="d-flex">
                        <fe-label class="label">State Code</fe-label>
                        <v-text-field v-model="info.state" flat solo dense @change="update"/>
                    </div>
                    <div class="d-flex">
                        <fe-label class="label">Zip Code</fe-label>
                        <v-text-field
                            v-model="info.zip" flat solo dense @change="update"
                            type="number"
                            :rules="[rules.zipcode.regex]"
                        />
                    </div>
                    <div class="d-flex">
                        <fe-label class="label">Phone Number</fe-label>
                        <v-text-field v-model="info.phone_number" flat solo dense @change="update"/>
                    </div>
                    <div class="d-flex">
                        <fe-label class="label">Fax Number</fe-label>
                        <v-text-field v-model="info.fax_number" flat solo dense @change="update"/>
                    </div>
                    <div class="d-flex">
                        <fe-label class="label">Time Zone</fe-label>

                        <fe-remote-combo
                            url="crud.php?property=TimeZone&action=get&active=1"
                            rootProperty="time_zone"
                            itemText="time_zone"
                            itemValue="time_zone"
                            byId
                            v-model="info.time_zone"
                            @change="update"
                        />
                    </div>
                    <div>
                        <div class="d-flex">
                            <fe-label class="label d-flex">Email Sender Name<fe-info-tip class="ml-1" style="margin-top:-4px;" tooltip="You can add a sender alias for emails sent to guardians using smartFORMS (such as your school district name), but the actual email address (noreply@educlimber.com) cannot be adjusted. If left blank, only the no-reply@educlimber.com displays as the sender."></fe-info-tip></fe-label>
                            <v-text-field 
                                v-model="info.email_friendly_from" 
                                flat solo dense 
                                @change="update" 
                                hint="&lt;no-reply@educlimber.com&gt;" 
                                persistent-hint 
                                class="district-info-email-sender" 
                                :rules="[noQuotesRule, maxLengthRule]"
                                data-test="email-sender-name-input-field"
                            />
                        </div>
                    </div>

                    <div v-if="$hasSecurity('UPLOAD_DATA')">
                        <div style="font-size: 14px;"><b>sFTP Settings</b></div>
                        <div class="d-flex">
                            <fe-label>sFTP Username</fe-label>
                            <div class="ml-11 mt-1">{{info.sftp_username}}</div>
                        </div>

                        <div class="d-flex">
                            <fe-label>sFTP Password</fe-label>
                            <div id="sftp_password" class="flex-column ml-11 mt-1">
                                <div style="height: 20px;" v-safe-html="pwdDisplay.name"></div>
                                <fe-btn
                                    style="margin-left: -8px;"
                                    :disabled="!pwdDisplay.set"
                                    :useIcon="showPassword ? 'far fa-eye-slash' : 'fas fa-eye'"
                                    usage="primary"
                                    @click="showPassword = !showPassword"
                                >
                                    {{ showPassword ? 'Hide Password' : 'ShowPassword' }}
                                </fe-btn>
                            </div>
                        </div>

                        <div class="d-flex">
                            <fe-label>sFTP Hostname</fe-label>
                            <div class="ml-10 mt-1">{{info.sftp_host}}</div>
                        </div>

                        <div class="d-flex">
                            <fe-label style="width: 80px;">sFTP Port</fe-label>
                            <div class="ml-11 mt-1">{{info.sftp_port}}</div>
                        </div>
                    </div>
                </v-col>
                <v-col cols="6">
                    <div v-if="manageDistrict" class="d-flex">
                        <fe-label class="label">Upload Directory</fe-label>
                        <v-text-field
                            v-model="info.upload_dir"
                            flat solo dense
                            @change="update"
                        />
                    </div>
                    <div v-if="manageDistrict" class="d-flex">
                        <fe-label class="label">Default FTP Directory</fe-label>
                        <v-text-field
                            v-model="info.ftp_dir"
                            flat solo dense
                            @change="update"
                        />
                    </div>
                    <div v-if="manageDistrict" class="d-flex">
                        <fe-label class="label">Login Format</fe-label>
                        <v-text-field
                            v-model="info.login_format"
                            flat solo dense
                            @change="update"
                        />
                    </div>
                    <div class="d-flex">
                        <fe-label class="label">Email Domain</fe-label>
                        <v-text-field v-model="info.email_domain" prefix="@" flat solo dense @change="update" />
                    </div>
                    <div class="d-flex">
                        <fe-label class="label">Alternate Domain</fe-label>
                        <v-text-field v-model="info.alt_domain" prefix="@" flat solo dense @change="update"/>
                    </div>
                    <div v-if="manageDistrict" class="d-flex">
                        <fe-label class="label">Logo Url</fe-label>
                        <v-text-field
                            v-model="info.logo_file"
                            flat solo dense
                            @change="update"
                        />
                    </div>
                    <div class="d-flex mb-7">
                        <fe-label class="label">Report PDF Section Header Color</fe-label>
                        <color-picker dataTest="district-info-primary" v-model="info.primary_color" @update="updateColor($event, 'primary_color')"/>
                    </div>
                    <div class="d-flex">
                        <fe-label class="label">Report PDF Section Header Text</fe-label>
                        <color-picker dataTest="district-info-secondary" v-model="info.secondary_color" @update="updateColor($event, 'secondary_color')"/>
                    </div>
                </v-col>
            </v-row>

            <v-row class="flex-grow-1">
                <v-col cols="12">
                    <div style="font-size: 14px;"><b>District Settings</b></div>
                </v-col>

                <v-col cols="3">
                    <div class="d-flex">
                        <fe-label id="allow-google-calendar-label" class="label toggle">Allow Google Calendar</fe-label>
                        <fe-switch aria-labelledby="allow-google-calendar-label" data-test="google-calendar-switch" v-model="info.google_calendar_enabled" @input="update('google_calendar_enabled')"/>
                    </div>
                </v-col>
                <v-col cols="3">
                    <div class="d-flex">
                        <fe-label id="adjusted-attendance-rate-label" class="label toggle">Enable Adjusted Attendance Rate</fe-label>
                        <fe-switch
                            aria-labelledby="adjusted-attendance-rate-label"
                            data-test="adjusted-attendance-rate-switch"
                            v-model="prefs.ATTENDANCE_ADJUSTED_RATE"
                            @change="updatePref($event, 'ATTENDANCE_ADJUSTED_RATE')"
                        />
                    </div>
                </v-col>
                <v-col cols="3">
                    <div class="d-flex">
                        <fe-label id="benchmark-chart-lines-label" class="label toggle">Do not connect scores on benchmark charts</fe-label>
                        <fe-switch aria-labelledby="benchmark-chart-lines-label" data-test="benchmark-chart-lines-switch" v-model="info.hide_score_chart_lines" @input="update"/>
                    </div>
                </v-col>
                <v-col cols="3" v-if="prefs.CONSOLE_ATTENDANCE != null">
                    <div class="d-flex">
                        <fe-label id="console-sis-calculation-label" class="label toggle">Enable Console SIS Calculation</fe-label>
                        <fe-switch
                            aria-labelledby="console-sis-calculation-label"
                            data-test="console-sis-calculation-switch"
                            v-model="prefs.CONSOLE_ATTENDANCE"
                            @change="updatePref($event, 'CONSOLE_ATTENDANCE')"
                        />
                    </div>
                </v-col>
            </v-row>

            <v-row v-if="manageAiFeatures && anyAiToggleVisible" class="flex-grow-1">
                <v-col cols="12">
                    <div style="font-size: 14px;"><b>AI Settings</b></div>
                </v-col>

                <v-col cols="3" v-if="students3dNLFAgentFlag">
                    <div class="d-flex">
                        <fe-label id="students3d-agent-label" class="label toggle">Enable Students 3D Agent</fe-label>
                        <fe-switch
                            aria-labelledby="students3d-agent-label"
                            data-test="students3d-agent-switch"
                            v-model="prefs.STUDENTS_THREED_AGENT_ENABLED"
                            @change="updatePref($event, 'STUDENTS_THREED_AGENT_ENABLED')"
                        />
                    </div>
                </v-col>
            </v-row>

            <v-row v-if="manageDistrict" class="flex-grow-1">
                <v-col cols="12">
                    <div style="font-size: 14px;"><b>Support Settings</b></div>
                </v-col>

                <v-col cols="3">
                    <div class="d-flex">
                        <fe-label id="keep-rosters-current-label" class="label toggle">Keep Rosters Current</fe-label>
                        <fe-switch
                            aria-labelledby="keep-rosters-current-label"
                            data-test="keep-rosters-current-switch"
                            v-model="prefs.ROSTER_PURGE"
                            @change="updatePref($event, 'ROSTER_PURGE')"
                        />
                    </div>

                    <div class="d-flex">
                        <fe-label id="ps-portal-label" class="label toggle">PS Portal</fe-label>
                        <fe-switch aria-labelledby="ps-portal-label" data-test="ps-portal-switch" v-model="info.psportal_enabled" @input="update"/>
                    </div>

                    <div class="d-flex">
                        <fe-label id="disable-local-login-label" class="label toggle">Disable Local Login</fe-label>
                        <fe-switch
                            aria-labelledby="disable-local-login-label"
                            data-test="disable-local-login-switch"
                            v-model="prefs.DISABLE_LOCAL_LOGIN"
                            @change="updatePref($event, 'DISABLE_LOCAL_LOGIN')"
                        />
                    </div>
                </v-col>

                <v-col cols="3">
                    <div class="d-flex">
                        <fe-label id="achievement-dashboard-label" class="label toggle">Achievement Dashboard</fe-label>
                        <fe-switch aria-labelledby="achievement-dashboard-label" data-test="achievement-dashboard-switch" v-model="info.dashboard_enabled" @input="update"/>
                    </div>

                    <div class="d-flex">
                        <fe-label id="users-default-inactive-label" class="label toggle">Users Default to Inactive</fe-label>
                        <fe-switch
                            aria-labelledby="users-default-inactive-label"
                            data-test="users-default-inactive-switch"
                            v-model="prefs.IMPORT_USERS_INACTIVE"
                            @change="updatePref($event, 'IMPORT_USERS_INACTIVE')"
                        />
                    </div>

                    <div class="d-flex">
                        <fe-label class="label toggle">District Active</fe-label>
                        <span v-if="info.active" style="color: #095C2A;"><i class="far fa-check mr-2" ></i>Active</span>
                        <span v-else style="color: #BF0D00;"><i class="far fa-times mr-1" ></i>Inactive</span>
                    </div>
                </v-col>

                <v-col cols="3">
                    <div class="d-flex">
                        <fe-label id="students-default-inactive-label" class="label toggle">Students Default Inactive</fe-label>
                        <fe-switch
                            aria-labelledby="students-default-inactive-label"
                            data-test="students-default-inactive-switch"
                            v-model="prefs.IMPORT_STUDENTS_INACTIVE"
                            @change="updatePref($event, 'IMPORT_STUDENTS_INACTIVE')"
                        />
                    </div>

                    <div class="d-flex">
                        <fe-label id="enable-ie-sso-label" class="label toggle">Enable IE SSO</fe-label>
                        <fe-tooltip :tooltip="'Requires IMT Client ID'" direction="top" :disabled="info.ie_client_uuid ? true : false">
                            <fe-switch
                                    aria-labelledby="enable-ie-sso-label"
                                    data-test="enable-ie-sso-switch"
                                    v-model="info.authx_enabled"
                                    :true-value="1"
                                    :false-value="0"
                                    :disabled="!info.ie_client_uuid"
                                    @input="update"
                            />
                        </fe-tooltip>
                    </div>
                </v-col>

                <v-col cols="3">
                    <div class="d-flex">
                        <fe-label id="edfi-label" class="label toggle">Ed-Fi</fe-label>
                        <fe-switch aria-labelledby="edfi-label" data-test="edfi-switch" v-model="info.edfi_enabled" @input="update"/>
                    </div>

                    <div class="d-flex">
                        <fe-label id="hispanic-indicator-label" class="label toggle">Utilize Hispanic Indicator</fe-label>
                        <fe-switch
                            aria-labelledby="hispanic-indicator-label"
                            data-test="hispanic-indicator-switch"
                            v-model="prefs.ENABLE_HISPANIC_INDICATOR"
                            @change="updatePref($event, 'ENABLE_HISPANIC_INDICATOR')"
                        />
                    </div>
                </v-col>
            </v-row>

            <v-row v-if="manageDistrict" class="flex-grow-1">
                <v-col cols="6">
                    <div class="d-flex">
                        <fe-label class="label">Salesforce Account Number</fe-label>
                        <v-text-field v-model="info.sf_number" flat solo dense @change="update"/>
                    </div>
                </v-col>
            </v-row>

            <v-row v-if="manageDistrict" class="flex-grow-1">
                <v-col cols="6">
                    <div class="d-flex">
                        <fe-label class="label">IMT Client ID</fe-label>
                        <v-text-field v-model="info.ie_client_uuid" flat solo dense @change="confirmFieldUpdate('ie_client_uuid')"/>
                    </div>
                </v-col>
            </v-row>
        </div>

        <fe-crud ref="crud" :config="crudConfig" @read="fetched" autoload/>
        <fe-crud ref="prefCrud" :config="prefCrudConfig" @read="prefsFetched" autoload/>
    </v-layout>
</template>

<script>
    import {mapState} from "vuex"
    import ColorPicker from '@/components/common/button/ColorPicker.vue'

    export default {
        name: "DistrictInfo",

        components: { ColorPicker },

        data() {
            return {
                ready: false,
                updateCallback: {
                    google_calendar_enabled: (v) => this.$store.commit('global/updateDistrictInfo', { flag: 'google_calendar_enabled', value: v ? 1 : 0 }),
                },
                info: {
                    id: undefined,
                    active: false,
                    attendance_enabled: false,
                    city: undefined,
                    dashboard_enabled: false,
                    edfi_enabled: false,
                    email_domain: undefined,
                    alt_domain: undefined,
                    fax_number: undefined,
                    ftp_dir: undefined,
                    google_calendar_enabled: false,
                    hide_score_chart_lines: false,
                    login_format: undefined,
                    login_id: undefined,
                    login_message: undefined,
                    logo_file: undefined,
                    name: undefined,
                    phone_number: undefined,
                    primary_color: undefined,
                    psportal_enabled: false,
                    secondary_color: undefined,
                    state: undefined,
                    street: undefined,
                    time_zone: undefined,
                    upload_dir: undefined,
                    zip: undefined,
                    sftp_username: '',
                    sftp_password: '',
                    sftp_host: '',
                    sftp_port: '',
                    sf_number: undefined,
                    ie_client_uuid: undefined,
                    authx_enabled: undefined,
                    email_friendly_from: undefined,
                },
                prefs: {},
                random: '',
                manageDistrict: false,
                manageAiFeatures: false,
                showPassword: false,
                setTimeInstance: undefined,
                requiredRule: [v => !_.isEmpty(v) || 'This field is required'],
                rules: {
                    zipcode: {
                        regex: v =>  /^\b\d{5}?\b$/.test(v) || 'Zip code must contain 5 integers.'
                    }

                }
            }
        },

        computed: {
            ...mapState('manage', ['districtPreferences']),

            ...mapState('global', ['hideNav', 'shareableStores']),

            pwdDisplay() {
                let pwd = this.info.sftp_password
                let display = {
                    name: '',
                    set: true
                }
                if (this.$_.isEmpty(pwd)) {
                    display.name = "No Password Set"
                    display.set = false
                } else if (this.showPassword) {
                    display.name = pwd
                } else {
                    display.name = pwd.replace(/./g, '&bull;')
                }
                return display
            },

            crudConfig() {
                return this.$_.cloneDeep(this.$models.districtInfo)
            },

            prefCrudConfig() {
                let cfg = this.$_.cloneDeep(this.$models.preferences)
                cfg.read.params.property = 'district'
                return cfg
            },

            students3dNLFAgentFlag() {
                return this.$store.getters['flags/flags']?.['students3D-search-agent'] === true
            },

            // OR every individual AI toggle's own flag - the section itself should only
            // show if at least one toggle inside it would actually render.
            anyAiToggleVisible() {
                return this.students3dNLFAgentFlag
            },

        },

        created() {
            this.manageDistrict = this.$checkSecurity('MANAGE_DISTRICT')
            this.manageAiFeatures = this.$checkSecurity('MANAGE_AI_FEATURES')
        },

        methods: {
            fetched(v) {
                if (v.length) {
                    this.$_.forEach(v[0], (value, key) => {
                        if (this.info.hasOwnProperty(key)) {
                            if (typeof this.info[key] === 'boolean') {
                                this.info[key] = !!value
                            } else {
                                this.info[key] = value
                            }
                        }
                        if (value?.length && (key === 'email_domain' || key === 'alt_domain')) {
                            this.info[key] = value.replace(/^@/g, '');
                        }
                    })

                    this.ready = true

                }

            },

            prefsFetched(v) {
                let prefs = {}
                let hasAdjustedRateValue = false
                v.forEach(pref => {
                    prefs[pref.code] = pref.user_value !== "0"
                    if (pref.code === 'ATTENDANCE_ADJUSTED_RATE') hasAdjustedRateValue = true
                })

                if (!hasAdjustedRateValue) {
                    this.updatePref(false, 'ATTENDANCE_ADJUSTED_RATE')
                    return
                }

                this.prefs = prefs

                this.$store.commit('set', {
                    module: 'manage',
                    state: 'districtPreferences',
                    value: prefs
                })
            },

            editorInput(v) {
                if (this.setTimeInstance) clearTimeout(this.setTimeInstance)
                if (this.$_.isEmpty(v)) {
                    this.setTimeInstance = setTimeout(this.update, 2000)
                }
            },

            update(cb) {
                let updatedInfo = this.$_.cloneDeep(this.info)
                updatedInfo.email_domain = updatedInfo.email_domain.length ? '@' + updatedInfo.email_domain : ''
                updatedInfo.alt_domain = updatedInfo.alt_domain?.length ? '@' + updatedInfo.alt_domain : ''
                if (!updatedInfo.ie_client_uuid) {
                    this.info.authx_enabled = false
                    updatedInfo.authx_enabled = 0
                }
                return this.$refs.crud.update(updatedInfo).then((resp) => {
                    if (cb && resp?.data?.success && this.updateCallback[cb]) {
                        this.updateCallback[cb](this.info[cb] ?? null)
                    }
                })
            },

            // A special version of update that prompts the user before changing
            confirmFieldUpdate(field) {
                let msg = 'Are you sure you want to update this field?'
                if (field == 'ie_client_uuid') {
                    msg = 'Changing the Client ID can impact integrations and lead to incorrect data being shared between clients.  Continue anyway?'
                }

                this.$messageBox({
                    title: 'Confirm Update',
                    persistent: true,
                    message: msg,
                    maxWidth: '500',
                    actions: [{
                        text: 'Cancel',
                        usage: "ghost",
                    }, {
                        text: 'Continue',
                        primary: true,
                        onClick: () => {
                            this.update()
                        }
                    }]
                })
            },

            updatePref(v, id) {
                let data = {}
                data[id] = v ? "1" : "0"
                let prefCrud = this.$refs.prefCrud
                prefCrud.update(data).then(() => {
                    prefCrud.read()
                })
            },

            handleLogoUpload() {
                let logoToUpload = this.$refs.logoInput.files[0];

                if (logoToUpload) {
                    let formData = new FormData()
                    formData.append('logoFile', logoToUpload)

                    this.$axios.post('logoUpload.php', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    })
                        .then(() => {
                            this.random = Math.floor(Math.random() * 1000)
                        })
                }
            },

            updateColor(color, prop) {
                if (prop && color) {
                    if (this.info.hasOwnProperty(prop) && this.info[prop] !== color) {
                        this.info[prop] = color
                        this.update()
                    }
                }
            },

            noQuotesRule(value) {
                // Regular expression to check for the presence of ' or "
                const hasQuotes = /['"]/.test(value)
                // If hasQuotes is true, return an error message, otherwise return true (valid)
                return !hasQuotes || 'Quotation marks ( \' or " ) are not allowed'
            },

            maxLengthRule(value) {
                return !value || value.length <= 100 || 'Input must be less than 100 characters'
            }

        }
    }
</script>

<style lang="scss" scoped>
    .label {
        width: 125px;
        font-size: 14px;

        &.toggle {
            width: 185px;
        }
    }
    .district-info-email-sender ::v-deep .v-messages__message {
        color: #495B8F;
    }

    // The real input is opacity:0 (Vuetify's styling), so the focus outline has to
    // target the visible track instead via the sibling selector.
    ::v-deep input[role="switch"]:focus-visible ~ .v-input--switch__track {
        outline: 2px solid #2B87FF !important;
        outline-offset: 2px;
        border-radius: 999px;
    }
</style>
